<template>
  <div class="generation-grid-wrap">
    <div class="generation-cloud-title">Настройки генерации контента</div>
    <div v-if="groupedGeneration.length" class="generation-groups">
      <div v-for="group in groupedGeneration" :key="group.type" class="generation-group">
        <div class="generation-settings-grid">
          <div v-for="def in group.items" :key="def.settingKey"
            :class="['generation-setting', { 'generation-setting--full': def.settingType === 'TEXT_AREA' }]">
            <small class="text-muted d-block">{{ def.description }}</small>
            <template v-if="def.settingType === 'DROP_DOWN'">
              <select class="form-select form-select-sm generation-select"
                :value="modelValue[def.settingKey]"
                @change="onChange(def.settingKey, $event.target.value)">
                <option v-for="opt in def.metadata.options" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </template>
            <template v-else-if="def.settingType === 'MULTI_SELECT'">
              <select class="form-select form-select-sm generation-multi-select" multiple
                :size="Math.min(8, Math.max(5, def.metadata?.options?.length || 5))"
                :value="toArray(modelValue[def.settingKey])"
                @change="onMultiChange(def.settingKey, $event.target)">
                <option v-for="opt in def.metadata.options" :key="opt" :value="opt">{{ opt }}</option>
              </select>
            </template>
            <template v-else-if="def.settingType === 'SLIDER'">
              <div class="slider-wrapper">
                <input type="range" class="form-range"
                  :min="def.metadata.min" :max="def.metadata.max" :step="def.metadata.step"
                  :value="modelValue[def.settingKey]"
                  @input="onChange(def.settingKey, Number($event.target.value))" />
                <span class="slider-value">{{ modelValue[def.settingKey] }}</span>
              </div>
            </template>
            <template v-else-if="def.settingType === 'CHECKBOX'">
              <div class="form-check generation-checkbox mt-1">
                <input :id="`gen-${def.settingKey}`" class="form-check-input" type="checkbox"
                  :checked="toBool(modelValue[def.settingKey])"
                  @change="onChange(def.settingKey, $event.target.checked)" />
                <label class="form-check-label" :for="`gen-${def.settingKey}`">Включено</label>
              </div>
            </template>
            <template v-else-if="def.settingType === 'TEXT_BOX'">
              <input type="text" class="form-control"
                :value="modelValue[def.settingKey]"
                @input="onChange(def.settingKey, $event.target.value)" />
            </template>
            <template v-else-if="def.settingType === 'TEXT_AREA'">
              <textarea class="form-control" :rows="getGenerationTextAreaRows(def)"
                :value="modelValue[def.settingKey]"
                @input="onChange(def.settingKey, $event.target.value)"></textarea>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-muted mb-3">{{ emptyText }}</div>
  </div>
</template>

<script setup>
// Renders the GENERATION-category settings grid for manual generation.
// v-model contract: { settingKey: value } — mirrors SettingDefForm so the parent
// owns the settings object and this component never mutates props directly.
const props = defineProps({
  groupedGeneration: { type: Array, default: () => [] },
  modelValue: { type: Object, default: () => ({}) },
  emptyText: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

function onChange(key, value) {
  emit('update:modelValue', { ...props.modelValue, [key]: value })
}

function onMultiChange(key, target) {
  const selected = Array.from(target.selectedOptions || []).map(o => o.value)
  emit('update:modelValue', { ...props.modelValue, [key]: selected })
}

function toArray(v) {
  if (Array.isArray(v)) return v
  if (v == null || v === '') return []
  return [v]
}

function toBool(v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase())
  return Boolean(v)
}

function getGenerationTextAreaRows(def) {
  const key = String(def?.settingKey || '').toLowerCase()
  const desc = String(def?.description || '').toLowerCase()
  const isTemplate =
    (key.includes('prompt') && (key.includes('template') || key.includes('pattern'))) ||
    (desc.includes('prompt') && desc.includes('template')) ||
    (desc.includes('шаблон') && desc.includes('запрос') && (desc.includes('ии') || desc.includes('ai')))
  return isTemplate ? 6 : 4
}
</script>

<style scoped>
.generation-cloud-title {
  display: inline-flex;
  align-items: center;
  margin: 0 0 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: linear-gradient(180deg, #f2f7ff 0%, #eaf2ff 100%);
  color: #2f4f88;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 10px rgba(49, 94, 227, 0.08);
}

.generation-select { max-width: 360px; }
.generation-multi-select { max-width: 420px; }

.generation-grid-wrap .form-control,
.generation-grid-wrap .form-select,
.generation-grid-wrap textarea.form-control { width: 100%; border-radius: 10px; }

.generation-grid-wrap .form-control:focus,
.generation-grid-wrap .form-select:focus {
  border-color: #95b8ff;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14);
}

.generation-settings-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}

.generation-groups {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.generation-setting {
  min-width: 0;
  border: 1px solid #e7edf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px 12px;
}

.generation-setting--full { grid-column: 1 / -1; }
.generation-setting small { margin-bottom: 6px; line-height: 1.3; }

.slider-wrapper { display: flex; gap: 10px; align-items: center; }
.slider-wrapper .form-range { flex: 1; }
.slider-wrapper .slider-value { min-width: 28px; white-space: nowrap; font-weight: 600; color: #354b74; }

.generation-checkbox .form-check-input { border-radius: 6px; width: 1.1rem; height: 1.1rem; }
.generation-checkbox .form-check-input:focus { box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14); }
.generation-checkbox .form-check-label { margin-left: 4px; font-size: 13px; color: #43567a; }

@media (max-width: 768px) {
  .generation-settings-grid { grid-template-columns: 1fr; }
}
</style>
